import { Button } from '@bambi/component-library/src/components/Button';
import { PencilIcon } from '@heroicons/react/24/outline';
import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { assignDefaultValues } from './assignDefaultValues';
import { PassengerFormController } from './PassengerFormController';
import { SlideInDialog } from 'src/common/dialogs/SlideInDialog';
import type {
  Passenger,
  PassengerRead,
} from 'src/common/external/bambi-api/bambiApi';
import { useConfirmClose } from 'src/common/modals/RequiresConfirmationModal/RequiresConfirmationModal';

interface PassengerFormDialogProps {
  passenger?: PassengerRead;
  open?: boolean;
  setOpen: (open: boolean) => void;
}

export function PassengerFormDialog({
  passenger,
  open,
  setOpen: setOpenInternal,
}: PassengerFormDialogProps) {
  const defaultValues = assignDefaultValues(passenger);
  const form = useForm<Passenger>({
    defaultValues,
  });

  const { isDirty } = form.formState;
  const { reset } = form;

  const shouldConfirmClose = useCallback(() => isDirty, [isDirty]);
  const [setOpen, onClose] = useConfirmClose({
    setOpen: setOpenInternal,
    onClose: reset,
    shouldConfirmClose,
  });

  return (
    <SlideInDialog
      open={open}
      setOpen={setOpen}
      onClose={onClose}
      ariaTitle="Passenger Form - Dialog"
      testId="passenger-form-dialog"
    >
      <FormProvider {...form}>
        <PassengerFormController setOpen={setOpen} passenger={passenger} />
      </FormProvider>
    </SlideInDialog>
  );
}

interface PassengerFormDialogTriggerProps {
  passenger?: PassengerRead;
}

export function PassengerFormDialogTrigger({
  passenger,
}: PassengerFormDialogTriggerProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      {passenger ? (
        <PencilIcon
          data-testid="edit-passenger"
          data-passengerid={passenger.id}
          className="cursor-pointer hover:text-mint"
          onClick={() => setOpen(true)}
        />
      ) : (
        <Button onClick={() => setOpen(true)} variant="primary">
          Add a Passenger
        </Button>
      )}
      <PassengerFormDialog
        open={open}
        setOpen={setOpen}
        passenger={passenger}
      />
    </>
  );
}
